import { useEffect, useState } from 'react';
import { Container, Dropdown, Offcanvas } from 'react-bootstrap';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { useClinic } from '../context/ClinicContext';
import { titleCase } from '../utils/format';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', roles: ['reception', 'doctor'] },
  { to: '/queue', label: "Today's Queue", roles: ['reception', 'doctor'] },
  { to: '/patients/new', label: 'New Patient', roles: ['reception'] },
  { to: '/patients', label: 'Patients', roles: ['reception', 'doctor'] },
  { to: '/follow-ups', label: 'Follow-ups', roles: ['reception', 'doctor'] },
  { to: '/dues', label: 'Pending Payments', roles: ['reception', 'doctor'] },
  { to: '/referral-clinics', label: 'Referral Clinics', roles: ['reception', 'doctor'] },
  { to: '/inventory', label: 'Medicine Inventory', roles: ['reception', 'doctor'] },
  { to: '/reports', label: 'Reports', roles: ['doctor'] },
];

function initialsOf(name = '') {
  return (
    name
      .replace(/^Dr\.?\s*/i, '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '?'
  );
}

export default function AppLayout() {
  const { user, logout, isDoctor } = useAuth();
  const { clinic } = useClinic();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Following a link on a phone should close the drawer behind it.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  const links = NAV.filter((l) => l.roles.includes(user.role));

  const brand = (
    // The logo already carries the clinic name, so it stands on its own here.
    <NavLink to="/dashboard" className="side-brand">
      <img src={logo} alt={clinic.clinicName} className="side-logo" />
    </NavLink>
  );

  const nav = (
    <nav className="side-nav">
      {links.map((l) => (
        <NavLink key={l.to} to={l.to} end={l.to === '/patients'} className="side-link">
          {l.label}
        </NavLink>
      ))}
    </nav>
  );

  const account = (
    <Dropdown drop="up" align="start" className="side-account">
      <Dropdown.Toggle as="button" type="button" className="side-account-btn" id="user-menu">
        <span className="side-avatar">{initialsOf(user.name)}</span>
        <span className="side-account-text">
          <span className="side-account-name">{user.name}</span>
          <span className="side-account-role">{titleCase(user.role)}</span>
        </span>
        <i className="bi bi-chevron-up side-account-caret" />
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {isDoctor && (
          <>
            <Dropdown.Item onClick={() => navigate('/masters')}>
              <i className="bi bi-capsule me-2" />
              Medicines &amp; Advice
            </Dropdown.Item>
            <Dropdown.Item onClick={() => navigate('/settings')}>
              <i className="bi bi-gear me-2" />
              Clinic Settings
            </Dropdown.Item>
            <Dropdown.Divider />
          </>
        )}
        <Dropdown.Item onClick={handleLogout} className="text-danger">
          <i className="bi bi-box-arrow-right me-2" />
          Sign Out
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        {brand}
        {nav}
        <div className="side-foot">{account}</div>
      </aside>

      <Offcanvas
        show={drawerOpen}
        onHide={() => setDrawerOpen(false)}
        className="app-sidebar app-sidebar-drawer"
        backdropClassName="app-drawer-backdrop"
      >
        {brand}
        {nav}
        <div className="side-foot">{account}</div>
      </Offcanvas>

      <div className="app-content">
        <header className="app-topbar">
          <button
            type="button"
            className="app-drawer-toggle"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <i className="bi bi-list" />
          </button>
          <img src={logo} alt={clinic.clinicName} className="app-topbar-logo" />
        </header>

        <main className="app-main">
          <Container fluid className="app-container">
            <Outlet />
          </Container>
        </main>
      </div>
    </div>
  );
}
